import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// material
import { Container, Stack, Button, Typography, Grid, Card } from '@mui/material';
// components
import { LoadingButton } from '@mui/lab';
import Page from '../components/Page';
import { ProductCard } from '../sections/@dashboard/products';
// mock
import { getServices } from '../store/actions/services';
import {
  getCurrentProcessor,
  getProcessors,
  updateCurrentServiceProcessor,
  updateServiceImages,
} from '../api/services';
import { BRAND_IMAGES } from '../utils/assets';
import { hasPermission, PERMISSIONS } from '../utils/hasPermission';

// ----------------------------------------------------------------------

export const groupBy = (key) => (array) =>
  array.reduce((objectsByKeyValue, obj) => {
    const value = obj[key];
    objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
    return objectsByKeyValue;
  }, {});

const PRODUCT_FILTER = {
  DATA: 'Data',
  AIRTIME: 'Airtime',
  ELECTRICITY: 'Electricity',
  CABLE: 'Cable TV',
  SCHOOL: 'School PIN',
};

export default function EcommerceShop() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const { services } = useSelector((state) => state.services);
  const [serviceProcessors, setServiceProcessors] = React.useState([]);
  const [currentServiceProcessor, setCurrentServiceProcessor] = React.useState('');
  const [currentTab, setCurrentTab] = useState(PRODUCT_FILTER.DATA);
  const [loading, setLoading] = React.useState(false);

  const [loadingImages, setLoadingImages] = React.useState(false);

  const data = services.filter((data) => data.service === 'Data');
  const cable = services.filter((data) => data.service === 'Cable');
  const exam = services.filter((data) => data.service === 'ResultCheck');
  const electricity = services.filter((data) => data.service === 'Electricity');
  const airtime = services.filter((data) => data.service === 'Airtime');

  const groupByCategory = groupBy('category');

  const dataService = groupByCategory(data);
  const cableService = groupByCategory(cable);
  const examService = groupByCategory(exam);
  const electricityService = groupByCategory(electricity);
  const airtimeService = groupByCategory(airtime);

  const handleGetCurrentProcessor = async () => {
    const response = await getCurrentProcessor();
    if (response.ok) {
      setCurrentServiceProcessor(response.data.data ? response.data.data.processor.name : 'gladtidingsdata');
    }
  };

  const handleGetProcessors = async () => {
    const response = await getProcessors();

    if (response.ok) {
      setServiceProcessors(response.data.data);
    }
  };

  const handleUpdateServiceProvider = async () => {
    setLoading(true);
    const response = await updateCurrentServiceProcessor(currentServiceProcessor);
    if (response.ok) {
      toast.success(response.data.message);
      setLoading(false);
      return;
    }

    toast.error(response.data.message);
    setLoading(false);
  };

  const navigateToServiceCategory = (path, service) => {
    navigate(`${path}?currentService=${service}`);
  };

  const handleUpdateServiceImages = async () => {
    setLoadingImages(true);
    const response = await updateServiceImages(BRAND_IMAGES);
    if (response.ok) {
      toast.success(response.data.message);
      setLoadingImages(false);
      dispatch(getServices());
      return;
    }

    toast.error(response.data.message);
    setLoadingImages(false);
  };

  React.useEffect(() => {
    dispatch(getServices());
    handleGetProcessors();
    handleGetCurrentProcessor();
  }, []);

  return (
    <Page title="Dashboard: update services">
      <Container>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Service Management
        </Typography>
        <Typography>Manage your services by updating, adding and changing processors</Typography>
        {hasPermission(user, PERMISSIONS.SERVICES_WRITE) && (
          <LoadingButton
            loading={loadingImages}
            disabled={loadingImages}
            variant="contained"
            onClick={handleUpdateServiceImages}
            sx={{ color: 'white', mt: 2 }}
          >
            Update Images
          </LoadingButton>
        )}
        <Grid container spacing={1} sx={{ mt: 2 }}>
          {['Data', 'Airtime', 'Electricity', 'Cable TV', 'School PIN'].map((item) => (
            <Grid key={item} item xs={6} sm={6} md={2}>
              <Card
                onClick={() => setCurrentTab(item)}
                sx={{
                  width: '100%',
                  height: '40px',
                  borderWidth: currentTab === item ? 1 : 0,
                  borderStyle: 'solid',
                  borderColor: 'blue',
                  cursor: 'pointer',
                }}
              >
                <Stack sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography>{item}</Typography>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
        {PRODUCT_FILTER.DATA === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Data Service
            </Typography>
            {hasPermission(user, PERMISSIONS.SERVICES_WRITE) && (
              <Stack direction={'row'} sx={{ pb: 2 }}>
                <Button
                  sx={{ color: 'white' }}
                  variant="contained"
                  onClick={() => navigate('/dashboard/service/add-data-product')}
                >
                  Create new Plan
                </Button>
              </Stack>
            )}
            <Grid container spacing={'10px'}>
              {Object.entries(dataService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.AIRTIME === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Airtime Service
            </Typography>

            <Grid container spacing={'10px'}>
              {Object.entries(airtimeService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.ELECTRICITY === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Electricity Service
            </Typography>

            <Grid container spacing={'10px'}>
              {Object.entries(electricityService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.CABLE === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage TV Cable Service
            </Typography>

            <Grid container spacing={'10px'}>
              {Object.entries(cableService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.SCHOOL === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage School Result Check Service
            </Typography>
            <Grid container spacing={'10px'}>
              {Object.entries(examService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>
    </Page>
  );
}
